import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const FeatureContext = createContext({ features: {}, alwaysOn: false, loaded: false, refresh: () => {} });

export function FeatureProvider({ children }) {
  const { user } = useAuth();
  const [features, setFeatures] = useState({});
  const [alwaysOn, setAlwaysOn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    if (!user) { setLoaded(true); return; }
    try {
      const r = await api.get('/features/me');
      setFeatures(r.data.features || {}); setAlwaysOn(!!r.data.always_on);
    } catch { /* fail-open — features default enabled if backend errors */ }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);
  return <FeatureContext.Provider value={{ features, alwaysOn, loaded, refresh: load }}>{children}</FeatureContext.Provider>;
}

export function useFeature(name) {
  const ctx = useContext(FeatureContext);
  if (!ctx.loaded) return true;   // don't hide until we know
  // Always respect individual feature flag values, even for always-on roles.
  // Some features (e.g., KRA with strict_roles=true) explicitly override always-on.
  return ctx.features[name] !== false;
}

export function useAllFeatures() {
  return useContext(FeatureContext);
}

export function FeatureGate({ feature, children, fallback = null }) {
  const enabled = useFeature(feature);
  return enabled ? children : fallback;
}
